import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import {
  RecoveryWipHeader,
  WipActionDescription,
  wipActionProps,
} from "@/features/recovery/recovery-wip";

/**
 * RCV-05 — Post-Recovery Audit Packet, ported from
 * `.design-ref/prototype/features/recovery/recovery-audit.tsx` (ALL-5825).
 *
 * No recovery backend exists in this environment, and this is a
 * custody-adjacent ceremony, so the port keeps the designer's structure — the
 * breadcrumb, the header with its Download PDF / Send to Support actions, the
 * Packet Details definition list, the Evidence Chain console, the Custodian
 * Signatures panel — and fills none of it: every packet field is the shared
 * "not available yet" marker, the signature list is a designed empty state,
 * the evidence-chain console carries no root or block reference, and both
 * actions are disabled. The designer's toasts ("Audit packet downloaded",
 * "Packet attached to support ticket #4421") claimed a download and a ticket
 * that never happened; they are gone rather than ported.
 */
export default function RecoveryAuditPage() {
  return (
    <div>
      <RecoveryWipHeader
        title="Recovery Audit Packet"
        description="The record of a completed recovery ceremony. This area has no backend in this environment yet."
        actions={
          <>
            <Button variant="secondary" {...wipActionProps()}>
              ⬇ Download PDF
            </Button>
            <Button variant="secondary" {...wipActionProps()}>
              Send to Support
            </Button>
          </>
        }
      />
      <WipActionDescription />

      <div className="mb-5 rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 text-base font-bold text-ink">Packet Details</div>
        <dl className="space-y-2.5 text-sm">
          <Row k="Requested By" />
          <Row k="Justification" />
          <Row k="Approval" />
          <Row k="Execution Window" />
          <Row k="Quorum" />
          <Row k="Key Fingerprint" />
          <Row k="Result" />
        </dl>
      </div>

      <div className="mb-5 overflow-hidden rounded-[10px] border border-line bg-console-bg">
        <div className="border-b border-line bg-console-panel px-4 py-2 font-mono text-xs text-console-muted">
          Evidence Chain
        </div>
        <div className="p-4 font-mono text-[13px] leading-relaxed text-console-muted">
          No evidence chain recorded.
        </div>
      </div>

      <div className="rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 text-base font-bold text-ink">Custodian Signatures</div>
        <EmptyState
          className="py-6"
          title="No custodian signatures to show"
          description="No recovery ceremony has been completed in this environment, so no custodian has signed a packet."
          meta="Signatures and their timestamps come from the recovery backend. Nothing here is estimated."
        />
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink">{v ?? <UnavailableValue />}</dd>
    </div>
  );
}
